import type { ReactNode } from "react";

import { useLoomControlSurfaceContext } from "./LoomControlSurfaceContext";
import { LoomControlSurfaceTutorial } from "./LoomControlSurfaceTutorial";
import type {
  LoomControlSurfaceActuationMode,
  LoomWingControlSurfaceDefinition,
} from "./loomWingControlSurfaceContract";
import "./loom-control-surfaces.css";

interface RangeFieldProps {
  children: ReactNode;
  max: number;
  min: number;
  onChange: (value: number) => void;
  step: number;
  testId: string;
  value: number;
}

function RangeField({ children, max, min, onChange, step, testId, value }: RangeFieldProps) {
  return <label className="loom-control-range">
    <span>{children}<output>{value.toFixed(step < 0.01 ? 3 : 2)}</output></span>
    <input
      data-testid={testId}
      max={max}
      min={min}
      onChange={(event) => onChange(Number(event.target.value))}
      step={step}
      type="range"
      value={value}
    />
  </label>;
}

function coupledLimits(control: LoomWingControlSurfaceDefinition, mode: "symmetric" | "antisymmetric") {
  const { port, starboard } = control.limitsDeg;
  return mode === "symmetric"
    ? { minimum: Math.max(port.minimum, starboard.minimum), maximum: Math.min(port.maximum, starboard.maximum) }
    : { minimum: Math.max(starboard.minimum, -port.maximum), maximum: Math.min(starboard.maximum, -port.minimum) };
}

export function LoomControlSurfacePanel() {
  const controlSurface = useLoomControlSurfaceContext();
  if (!controlSurface?.selectedWing) return null;
  const {
    collection, definitionRevision, document, error, evaluation, persistenceState, pose,
    poseRevision, selectedControl, selectedState,
  } = controlSurface;
  const update = (next: LoomWingControlSurfaceDefinition) => controlSurface.replaceControlSurface(next);
  const updateSpan = (part: "start" | "end", value: number) => selectedControl && update({
    ...selectedControl, spanBand: { ...selectedControl.spanBand, [part]: value },
  });
  const updateHinge = (
    station: "inboard" | "outboard",
    part: "xOverChord" | "skinHeightFraction",
    value: number,
  ) => selectedControl && update({
    ...selectedControl,
    hinge: { ...selectedControl.hinge, [station]: { ...selectedControl.hinge[station], [part]: value } },
  });
  const updateNose = <K extends keyof LoomWingControlSurfaceDefinition["noseCove"]>(
    key: K, value: LoomWingControlSurfaceDefinition["noseCove"][K],
  ) => selectedControl && update({ ...selectedControl, noseCove: { ...selectedControl.noseCove, [key]: value } });
  const health = evaluation.health;
  const unavailable = error ?? (evaluation.status === "error" ? evaluation.message ?? "Control-surface evaluation failed"
    : selectedControl && !definitionRevision
    ? "Control-surface definition receipt unavailable"
    : selectedControl && (!pose || !poseRevision) ? "Control-surface pose receipt unavailable" : null);
  const currentAngles = selectedState
    ? `Port ${selectedState.portDeflectionDeg.toFixed(1)}° · Starboard ${selectedState.starboardDeflectionDeg.toFixed(1)}°`
    : "Port — · Starboard —";
  return <section
    className="loom-control-surface-panel"
    data-control-id={selectedControl?.id ?? "none"}
    data-definition-revision={definitionRevision ?? "unavailable"}
    data-definition-schema={collection?.schema ?? "loom.wing-control-surface-collection/v1"}
    data-evaluation-status={evaluation.status}
    data-persistence-state={persistenceState}
    data-pose-revision={poseRevision ?? "unavailable"}
    data-testid="loom-control-surface-panel"
  >
    <header className="loom-control-surface-heading">
      <div><span>Wing child collection</span><strong>Control surfaces</strong></div>
      <button
        aria-label="Control surface"
        data-testid="loom-add-control-surface"
        disabled={!controlSurface.canAddControlSurface || collection?.controlSurfaces.length === 16}
        onClick={() => void controlSurface.addControlSurface()}
        type="button"
      >+ Control surface</button>
    </header>
    <p className="loom-control-visual-legend" data-testid="loom-control-visual-legend">
      Selected movable body uses a deliberate tint and outline; the straight hinge axis and span/hinge handles remain visible.
    </p>
    <LoomControlSurfaceTutorial />
    {collection?.controlSurfaces.length ? <div aria-label="Wing control surfaces" className="loom-control-surface-list">
      {collection.controlSurfaces.map((control) => <button
        aria-selected={control.id === selectedControl?.id}
        data-control-id={control.id}
        data-testid={`loom-control-surface-row-${control.id}`}
        key={control.id}
        onClick={() => controlSurface.selectControlSurface(control.id)}
        role="option"
        type="button"
      ><span>{control.label}</span><code>{control.spanBand.start.toFixed(2)}–{control.spanBand.end.toFixed(2)}</code></button>)}
    </div> : <p className="loom-control-empty">No movable controls. Add a plain trailing-edge control to this wing.</p>}
    {selectedControl && selectedState ? <div className="loom-control-surface-editor">
      <div className="loom-control-selected-line">
        <span>Selected control</span>
        <strong data-control-id={selectedControl.id} data-testid="loom-selected-control-label">{selectedControl.label}</strong>
        <code>{selectedControl.id}</code>
      </div>
      <fieldset data-control-id={selectedControl.id} data-span-end={selectedControl.spanBand.end} data-span-start={selectedControl.spanBand.start} data-testid="loom-control-span-handles">
        <legend>Clean-wing normalized reference arclength</legend>
        <RangeField max={selectedControl.spanBand.end - 0.0001} min={0} onChange={(value) => updateSpan("start", value)} step={0.01} testId="loom-control-span-start" value={selectedControl.spanBand.start}>Span start</RangeField>
        <RangeField max={1} min={selectedControl.spanBand.start + 0.0001} onChange={(value) => updateSpan("end", value)} step={0.01} testId="loom-control-span-end" value={selectedControl.spanBand.end}>Span end</RangeField>
      </fieldset>
      <fieldset data-control-id={selectedControl.id} data-root-height={selectedControl.hinge.inboard.skinHeightFraction} data-root-xc={selectedControl.hinge.inboard.xOverChord} data-tip-height={selectedControl.hinge.outboard.skinHeightFraction} data-tip-xc={selectedControl.hinge.outboard.xOverChord} data-testid="loom-control-hinge-handles">
        <legend>Straight 3D hinge definition</legend>
        <RangeField max={0.98} min={0.5} onChange={(value) => updateHinge("inboard", "xOverChord", value)} step={0.01} testId="loom-control-hinge-root-xc" value={selectedControl.hinge.inboard.xOverChord}>Inboard x/c</RangeField>
        <RangeField max={0.98} min={0.5} onChange={(value) => updateHinge("outboard", "xOverChord", value)} step={0.01} testId="loom-control-hinge-tip-xc" value={selectedControl.hinge.outboard.xOverChord}>Outboard x/c</RangeField>
        <RangeField max={1} min={0} onChange={(value) => updateHinge("inboard", "skinHeightFraction", value)} step={0.01} testId="loom-control-hinge-root-height" value={selectedControl.hinge.inboard.skinHeightFraction}>Inboard lower→upper height</RangeField>
        <RangeField max={1} min={0} onChange={(value) => updateHinge("outboard", "skinHeightFraction", value)} step={0.01} testId="loom-control-hinge-tip-height" value={selectedControl.hinge.outboard.skinHeightFraction}>Outboard lower→upper height</RangeField>
      </fieldset>
      <fieldset className="loom-control-nose-cove">
        <legend>Concentric nose / cove</legend>
        <label><span>Closure</span><select value={selectedControl.noseCove.kind} onChange={(event) => {
          const kind = event.target.value as "sealed" | "positive-gap";
          update({ ...selectedControl, noseCove: { ...selectedControl.noseCove, kind, gapRatio: kind === "sealed" ? 0 : Math.max(0.005, selectedControl.noseCove.gapRatio) } });
        }}><option value="sealed">Sealed</option><option value="positive-gap">Positive gap</option></select></label>
        <RangeField max={0.05} min={selectedControl.noseCove.kind === "sealed" ? 0 : 0.0001} onChange={(value) => updateNose("gapRatio", value)} step={0.001} testId="loom-control-gap-ratio" value={selectedControl.noseCove.gapRatio}>Gap / chord</RangeField>
        <RangeField max={0.05} min={0} onChange={(value) => updateNose("filletRatio", value)} step={0.001} testId="loom-control-fillet-ratio" value={selectedControl.noseCove.filletRatio}>Fillet / chord</RangeField>
        <RangeField max={0.05} min={0} onChange={(value) => updateNose("endGapRatio", value)} step={0.001} testId="loom-control-end-gap-ratio" value={selectedControl.noseCove.endGapRatio}>End gap / span</RangeField>
      </fieldset>
      <div
        className="loom-control-limits"
        data-port-max={selectedControl.limitsDeg.port.maximum}
        data-port-min={selectedControl.limitsDeg.port.minimum}
        data-starboard-max={selectedControl.limitsDeg.starboard.maximum}
        data-starboard-min={selectedControl.limitsDeg.starboard.minimum}
        data-testid="loom-control-limits"
      ><span>Per-instance limits</span><strong>Port {selectedControl.limitsDeg.port.minimum}°…{selectedControl.limitsDeg.port.maximum}°</strong><strong>Starboard {selectedControl.limitsDeg.starboard.minimum}°…{selectedControl.limitsDeg.starboard.maximum}°</strong></div>
      <label className="loom-control-mode"><span>Actuation mode</span><select
        data-testid="loom-control-actuation-mode"
        onChange={(event) => controlSurface.setActuationMode(event.target.value as LoomControlSurfaceActuationMode)}
        value={selectedState.mode}
      ><option value="independent">Independent</option><option value="symmetric">Symmetric</option><option value="antisymmetric">Antisymmetric</option></select></label>
      {selectedState.mode === "independent" ? <div className="loom-control-pose-grid">
        <RangeField max={selectedControl.limitsDeg.port.maximum} min={selectedControl.limitsDeg.port.minimum} onChange={(value) => controlSurface.setIndependentDeflection("port", value)} step={1} testId="loom-control-port-deflection" value={selectedState.portDeflectionDeg}>Port deflection (TE-down +)</RangeField>
        <RangeField max={selectedControl.limitsDeg.starboard.maximum} min={selectedControl.limitsDeg.starboard.minimum} onChange={(value) => controlSurface.setIndependentDeflection("starboard", value)} step={1} testId="loom-control-starboard-deflection" value={selectedState.starboardDeflectionDeg}>Starboard deflection (TE-down +)</RangeField>
      </div> : (() => {
        const limits = coupledLimits(selectedControl, selectedState.mode);
        return <RangeField max={limits.maximum} min={limits.minimum} onChange={(value) => controlSurface.setCoupledDeflection(value)} step={1} testId="loom-control-coupled-deflection" value={selectedState.starboardDeflectionDeg}>Command deflection (local TE-down +)</RangeField>;
      })()}
      <output data-port-angle-deg={selectedState.portDeflectionDeg} data-starboard-angle-deg={selectedState.starboardDeflectionDeg} data-testid="loom-control-current-angles">{currentAngles}</output>
      <div
        className="loom-control-health"
        data-collision={health?.collision ?? "unavailable"}
        data-gap={health?.gap ?? "unavailable"}
        data-gap-evaluation={health ? "nominal-three-section-sampled" : "unavailable"}
        data-hinge={health?.hinge ?? "unavailable"}
        data-motion={health?.motion ?? "unavailable"}
        data-testid="loom-control-health"
      >Motion {health?.motion ?? "unavailable"} · gap {health?.gap ?? "unavailable"}{health ? `, nominal ${health.minimumGapM.toExponential(2)} m` : ""} · {health?.collision === "no-overlap-in-sampled-sections" ? "no overlap in sampled sections" : `collision ${health?.collision ?? "unavailable"}`} · hinge {health?.hinge ?? "unavailable"} · {health ? `${health.fidelity}, max axis deviation ${health.maxIntermediateDeviationM.toExponential(2)} m` : "evaluation unavailable"}</div>
    </div> : null}
    {unavailable ? <p className="loom-control-unavailable" data-testid="loom-control-unavailable" role="alert">{unavailable}</p> : null}
    <pre data-testid="loom-control-definition-json" hidden>{JSON.stringify(document)}</pre>
  </section>;
}
